import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../contexts/NotificationContext';
import api from '../services/api';
import normalizeImageFile from '../utils/normalizeImageFile';
import { FaSearch, FaComments, FaBell, FaBars, FaTimes, FaCog, FaBookmark, FaUsers, FaUser, FaChevronDown, FaPlus, FaSignOutAlt, FaShoppingCart, FaStore, FaLayerGroup, FaHome, FaIdCard } from 'react-icons/fa';
import ImageCropper from './ImageCropper';
import IdentifyStatus from './IdentifyStatus';
import { useCardAutoIdentify, fillBlankFields } from '../hooks/useCardAutoIdentify';
import './Navbar.css';
import { getBadgeStyle, formatNumberedProfile } from '../utils/avatarStyles';

const EMPTY_QUICK_ADD_FIELDS = {
  year: '', brand: '', set_name: '', card_number: '', sport: '',
  grade: '', grading_company: '', notes: '',
};

// 16px keeps iOS Safari from auto-zooming the page on focus.
const quickAddInputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px',
  fontSize: '16px', boxSizing: 'border-box', background: '#fff',
};
const quickAddLabelStyle = { display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' };
const quickAddRemoveStyle = {
  marginTop: '6px', marginLeft: '6px', padding: '5px 12px', border: '1px solid #e5484d', borderRadius: '6px',
  background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#e5484d', fontWeight: '600',
};

const Navbar = () => {
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();
  const { unreadCount: realtimeUnreadCount, refreshUnreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingRoute = location.pathname === '/';
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Ensure Sign Up button styles apply immediately
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .navbar .navbar-container .navbar-menu .btn-primary,
      .navbar .navbar-container .navbar-menu a.btn-primary,
      .navbar .navbar-container .navbar-menu .navbar-signup-btn {
        padding: 8px 16px !important;
        font-size: 14px !important;
        min-height: 36px !important;
        height: auto !important;
        width: auto !important;
        max-width: fit-content !important;
        line-height: 1.4 !important;
        display: inline-flex !important;
        flex-shrink: 0 !important;
        box-sizing: border-box !important;
      }
      @media (max-width: 768px) {
        .navbar .navbar-container .navbar-menu .btn-primary,
        .navbar .navbar-container .navbar-menu a.btn-primary,
        .navbar .navbar-container .navbar-menu .navbar-signup-btn {
          width: 100% !important;
          min-height: 44px !important;
          max-width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddImage, setQuickAddImage] = useState(null);
  const [quickAddImagePreview, setQuickAddImagePreview] = useState(null);
  const [quickAddBackImage, setQuickAddBackImage] = useState(null);
  const [quickAddBackImagePreview, setQuickAddBackImagePreview] = useState(null);
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [quickAddCropSide, setQuickAddCropSide] = useState(null); // 'front' | 'back' | null
  const [quickAddCropSrc, setQuickAddCropSrc] = useState(null);
  // Detail fields beyond the name; auto-identify fills blanks from the photos.
  const [quickAddFields, setQuickAddFields] = useState(EMPTY_QUICK_ADD_FIELDS);
  const { identifying: quickAddIdentifying, rereading: quickAddRereading, note: quickAddIdentifyNote } = useCardAutoIdentify({
    front: quickAddImage,
    back: quickAddBackImage,
    paused: !!quickAddCropSide,
    onResult: (card) => {
      setQuickAddName(prev => (prev.trim() ? prev : (card.player_name || '')));
      setQuickAddFields(prev => fillBlankFields(prev, card));
    },
  });
  const userMenuRef = useRef(null);
  const userMenuTimeoutRef = useRef(null);
  const cameraInputRef = useRef(null);
  const backImageInputRef = useRef(null);
  const [cartCount, setCartCount] = useState(0);

  // Use real-time unread count from NotificationContext
  useEffect(() => {
    if (isAuthenticated && realtimeUnreadCount !== undefined) {
      setUnreadCount(realtimeUnreadCount);
    }
  }, [isAuthenticated, realtimeUnreadCount]);

  useEffect(() => {
    if (isAuthenticated) {
      // Initial fetch
      fetchUnreadMessageCount();
      // Refresh message count every 30 seconds (notifications are real-time now)
      const interval = setInterval(() => {
        fetchUnreadMessageCount();
      }, 30000);
      
      // Listen for custom event when notifications are marked as read
      const handleNotificationsMarkedRead = () => {
        refreshUnreadCount();
      };
      window.addEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
      };
    }
  }, [isAuthenticated, refreshUnreadCount]);

  // Fetch cart count helper
  const fetchCartCount = async () => {
    if (!isAuthenticated) {
      setCartCount(0);
      return;
    }
    try {
      const response = await api.get('/cards/marketplace/cart/');
      const items = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setCartCount(items.length || 0);
    } catch (err) {

    }
  };

  // Initial cart count + listen for cart updates
  useEffect(() => {
    if (!isAuthenticated) {
      setCartCount(0);
      return;
    }

    fetchCartCount();

    const handleCartUpdated = () => {
      fetchCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdated);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdated);
    };
  }, [isAuthenticated]);

  const fetchUnreadMessageCount = async () => {
    try {
      const response = await api.get('/messages/messages/unread_count/');
      setUnreadMessageCount(response.data.unread_count || 0);
    } catch (err) {

    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Handle keyboard navigation for mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
      if (e.key === 'Escape' && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Trap focus within menu when open
      const menu = document.querySelector('.navbar-menu.mobile-open');
      if (menu) {
        const focusableElements = menu.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen, userMenuOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userMenuTimeoutRef.current) {
        clearTimeout(userMenuTimeoutRef.current);
      }
    };
  }, []);

  const handleLogoutClick = async () => {
    closeMobileMenu();
    setUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const closeQuickAdd = () => {
    setShowQuickAdd(false);
    setQuickAddName('');
    setQuickAddImage(null);
    setQuickAddImagePreview(null);
    setQuickAddBackImage(null);
    setQuickAddBackImagePreview(null);
    setQuickAddCropSide(null);
    setQuickAddCropSrc(null);
    setQuickAddFields(EMPTY_QUICK_ADD_FIELDS);
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    setQuickAddSubmitting(true);
    try {
      const data = new FormData();
      data.append('player_name', quickAddName.trim() || 'Card');
      if (quickAddImage) data.append('front_image', quickAddImage);
      if (quickAddBackImage) data.append('back_image', quickAddBackImage);
      Object.entries(quickAddFields).forEach(([field, value]) => {
        if (String(value).trim() !== '') data.append(field, String(value).trim());
      });
      await api.post('/cards/my-cards/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      closeQuickAdd();
      window.dispatchEvent(new CustomEvent('cardAdded'));
      navigate('/my-cards');
    } catch (err) {

    } finally {
      setQuickAddSubmitting(false);
    }
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-container">
        <Link to="/feed" className="navbar-brand" onClick={closeMobileMenu} aria-label="Pop1™ - Go to feed">
          <img src="/pop1-logo-new.jpeg" alt="Pop1™ logo" className="navbar-logo" />
          <span className="navbar-tagline">A community for card collectors</span>
        </Link>
        {!authLoading && isAuthenticated && (
          <>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
              {!mobileMenuOpen && (unreadCount > 0 || unreadMessageCount > 0) && (
                <span className="hamburger-unread-dot" />
              )}
            </button>
            <div className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`} role="menu">
              {/* Mobile-first: Profile, My Cards, Notifications at top */}
              {mobileMenuOpen && (
                <Link to="/profile" className="navbar-link" onClick={closeMobileMenu} role="menuitem">
                  <FaUser style={{ marginRight: '4px' }} />
                  Profile
                </Link>
              )}
              {mobileMenuOpen && (
                <Link to="/my-cards" className="navbar-link" onClick={closeMobileMenu} role="menuitem">
                  <FaLayerGroup style={{ marginRight: '4px' }} />
                  My Cards
                </Link>
              )}
              {mobileMenuOpen && (
                <Link
                  to="/notifications"
                  className="navbar-link notification-link"
                  onClick={closeMobileMenu}
                  role="menuitem"
                >
                  <span className="navbar-icon-wrap">
                    <FaBell />
                    {unreadCount > 0 && (
                      <span className="notification-badge" aria-label={`${unreadCount} unread notifications`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="navbar-link-mobile-label">Notifications</span>
                </Link>
              )}
              {/* Primary Navigation */}
              <Link to="/feed" className="navbar-link" onClick={closeMobileMenu} role="menuitem"><FaHome style={{ marginRight: '4px' }} />Feed</Link>
              {!mobileMenuOpen && (
                <Link to="/my-cards" className="navbar-link" onClick={closeMobileMenu} role="menuitem"><FaLayerGroup style={{ marginRight: '4px' }} />My Cards</Link>
              )}
              <Link to="/marketplace" className="navbar-link" onClick={closeMobileMenu} role="menuitem">
                <FaStore style={{ marginRight: '4px' }} />
                Marketplace
              </Link>
              <Link to="/groups" className="navbar-link" onClick={closeMobileMenu} role="menuitem">
                <FaUsers style={{ marginRight: '4px' }} />
                Groups
              </Link>

              {/* Icon Actions - labels shown on mobile via CSS (.navbar-link-mobile-label, .sr-only) */}
              <Link 
                to="/create-post" 
                className="navbar-link notification-link"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="Create a new post"
                onClick={closeMobileMenu}
                aria-label="Create a new post"
                role="menuitem"
              >
                <span className="navbar-icon-wrap">
                  <FaPlus />
                </span>
                <span className="sr-only navbar-link-mobile-label">Create Post</span>
              </Link>
              <Link
                to="/cart"
                className="navbar-link notification-link"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="View cart"
                onClick={closeMobileMenu}
                aria-label={`Cart${cartCount > 0 ? `. ${cartCount} items` : ''}`}
                role="menuitem"
              >
                <span className="navbar-icon-wrap">
                  <FaShoppingCart />
                  {cartCount > 0 && (
                    <span className="notification-badge" aria-label={`${cartCount} items in cart`}>
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </span>
                <span className="navbar-link-mobile-label">Cart</span>
              </Link>
              <Link 
                to="/search" 
                className="navbar-link notification-link"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="Search users, posts, and cards"
                onClick={closeMobileMenu}
                aria-label={`Search users, posts, and cards${unreadMessageCount > 0 ? `. ${unreadMessageCount} unread messages` : ''}`}
                role="menuitem"
              >
                <span className="navbar-icon-wrap">
                  <FaSearch />
                </span>
                <span className="navbar-link-mobile-label">Search</span>
              </Link>
              <Link
                to="/messages"
                className="navbar-link notification-link"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="Messages"
                onClick={closeMobileMenu}
                aria-label={`Messages${unreadMessageCount > 0 ? `. ${unreadMessageCount} unread` : ''}`}
                role="menuitem"
              >
                <span className="navbar-icon-wrap">
                  <FaComments />
                  {unreadMessageCount > 0 && (
                    <span className="notification-badge" aria-label={`${unreadMessageCount} unread messages`}>
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                </span>
                <span className="navbar-link-mobile-label">Messages</span>
              </Link>
              {!mobileMenuOpen && (
                <Link
                  to="/notifications"
                  className="navbar-link notification-link"
                  data-tooltip-id="main-tooltip"
                  data-tooltip-content="Notifications"
                  onClick={closeMobileMenu}
                  aria-label={`Notifications${unreadCount > 0 ? `. ${unreadCount} unread` : ''}`}
                  role="menuitem"
                >
                  <span className="navbar-icon-wrap">
                    <FaBell />
                    {unreadCount > 0 && (
                      <span className="notification-badge" aria-label={`${unreadCount} unread notifications`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="navbar-link-mobile-label">Notifications</span>
                </Link>
              )}

              {/* Quick-add card button — mobile only, opens camera */}
              <button
                className="navbar-link notification-link navbar-quick-add-btn"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="Quick add card"
                onClick={() => { cameraInputRef.current?.click(); }}
                aria-label="Quick add card to collection"
                role="menuitem"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <span className="navbar-icon-wrap">
                  <FaIdCard />
                </span>
                <span className="navbar-link-mobile-label">Add Card</span>
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const input = e.target;
                  const file = input.files[0] ? await normalizeImageFile(input.files[0]) : null;
                  if (file) {
                    setQuickAddImage(file);
                    setQuickAddImagePreview(URL.createObjectURL(file));
                    setShowQuickAdd(true);
                  }
                  closeMobileMenu();
                  input.value = '';
                }}
                style={{ display: 'none' }}
              />

              {/* User Menu Dropdown (Desktop) - Next to notifications icon */}
              <div 
                className="user-menu-container" 
                ref={userMenuRef}
                onMouseEnter={() => {
                  if (userMenuTimeoutRef.current) {
                    clearTimeout(userMenuTimeoutRef.current);
                    userMenuTimeoutRef.current = null;
                  }
                  setUserMenuOpen(true);
                }}
                onMouseLeave={() => {
                  // Add small delay to allow moving mouse to dropdown
                  userMenuTimeoutRef.current = setTimeout(() => {
                    setUserMenuOpen(false);
                  }, 100);
                }}
              >
                <button
                  className="user-menu-trigger"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                >
                  <div className="user-menu-avatar">
                    {user?.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={user.username}
                      />
                    ) : (
                      <div className="user-menu-avatar-placeholder">
                        {user?.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <FaChevronDown className={`user-menu-chevron ${userMenuOpen ? 'open' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="user-menu-dropdown">
                    <div className="user-menu-header">
                      <div className="user-menu-header-avatar">
                        {user?.avatar ? (
                          <img 
                            src={user.avatar} 
                            alt={user.username}
                          />
                        ) : (
                          <div className="user-menu-header-avatar-placeholder">
                            {user?.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                        )}
                      </div>
                      <div className="user-menu-header-info">
                        <div className="user-menu-username">@{user?.username}</div>
                        {user?.numbered_profile && (
                          <span 
                            className="profile-number-badge"
                            style={getBadgeStyle(user)}
                          >
                            {formatNumberedProfile(user.numbered_profile)}
                          </span>
                        )}
                      </div>
                    </div>
                  <div className="user-menu-divider"></div>
                  <Link to="/profile" className="user-menu-item" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); }}>
                    <FaUser /> Profile
                  </Link>
                  <Link to="/messages" className="user-menu-item" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); }}>
                    <FaComments /> Messages
                    {unreadMessageCount > 0 && (
                      <span style={{ marginLeft: 'auto', background: 'var(--color-error)', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '12px', fontWeight: 'bold' }}>
                        {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                      </span>
                    )}
                  </Link>
                    <Link to="/settings" className="user-menu-item" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); }}>
                      <FaCog /> Settings
                    </Link>
                    <div className="user-menu-divider"></div>
                    <button className="user-menu-item user-menu-logout" onClick={handleLogoutClick}>
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile: extra items (Create Post is in main list with label) */}
              {mobileMenuOpen && (
                <>
                  <Link to="/settings" className="navbar-link" onClick={closeMobileMenu} role="menuitem">
                    <FaCog style={{ marginRight: '8px' }} />
                    Settings
                  </Link>
                  <div className="navbar-user mobile-user-info">
                    <span className="sr-only">Logged in as</span>
                    @{user?.username}
                    {user?.numbered_profile && (
                      <span 
                        className="profile-number-badge" 
                        style={{ marginLeft: '8px', ...getBadgeStyle(user) }} 
                        aria-label={`Profile number ${formatNumberedProfile(user.numbered_profile)}`}
                      >
                        {formatNumberedProfile(user.numbered_profile)}
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={handleLogoutClick} 
                    className="btn btn-secondary" 
                    style={{ width: '100%', minHeight: '44px', marginTop: '12px' }}
                    aria-label="Logout"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* Quick-add card modal */}
        {showQuickAdd && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '72px' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} onClick={closeQuickAdd} />
            <div style={{ position: 'relative', background: '#fff', borderRadius: '12px', padding: '20px', width: '360px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 88px)', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '16px', fontWeight: '700' }}>Quick Add Card</h3>
              <form onSubmit={handleQuickAdd}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', justifyContent: 'center' }}>
                  {quickAddImagePreview && (
                    <div style={{ flex: quickAddBackImagePreview ? 1 : 'none', textAlign: 'center', maxWidth: quickAddBackImagePreview ? '50%' : '60%' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '600', color: '#888' }}>Front</p>
                      <div style={{ borderRadius: '8px', overflow: 'hidden', display: 'inline-block' }}>
                        <img
                          src={quickAddImagePreview}
                          alt="Card front"
                          style={{ maxWidth: '100%', maxHeight: '200px', display: 'block', borderRadius: '8px' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => { setQuickAddCropSide('front'); setQuickAddCropSrc(quickAddImagePreview); }}
                        style={{ marginTop: '6px', padding: '5px 12px', border: '1px solid var(--color-primary)', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: 'var(--color-primary)', fontWeight: '600' }}
                      >
                        Crop
                      </button>
                      <button
                        type="button"
                        onClick={() => { setQuickAddImage(null); setQuickAddImagePreview(null); }}
                        style={quickAddRemoveStyle}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {quickAddBackImagePreview && (
                    <div style={{ flex: 1, textAlign: 'center', maxWidth: '50%' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '600', color: '#888' }}>Back</p>
                      <div style={{ borderRadius: '8px', overflow: 'hidden', display: 'inline-block' }}>
                        <img
                          src={quickAddBackImagePreview}
                          alt="Card back"
                          style={{ maxWidth: '100%', maxHeight: '200px', display: 'block', borderRadius: '8px' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => { setQuickAddCropSide('back'); setQuickAddCropSrc(quickAddBackImagePreview); }}
                        style={{ marginTop: '6px', padding: '5px 12px', border: '1px solid var(--color-primary)', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: 'var(--color-primary)', fontWeight: '600' }}
                      >
                        Crop
                      </button>
                      <button
                        type="button"
                        onClick={() => { setQuickAddBackImage(null); setQuickAddBackImagePreview(null); }}
                        style={quickAddRemoveStyle}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  {!quickAddImage && (
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px dashed #ccc', borderRadius: '8px', background: '#fafafa', cursor: 'pointer', fontSize: '13px', color: '#666' }}
                    >
                      + Add Front Image
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => backImageInputRef.current?.click()}
                    style={{ width: '100%', padding: '8px', border: '1px dashed #ccc', borderRadius: '8px', background: '#fafafa', cursor: 'pointer', fontSize: '13px', color: '#666' }}
                  >
                    {quickAddBackImage ? 'Change Back Image' : '+ Add Back Image'}
                  </button>
                  <input
                    ref={backImageInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const input = e.target;
                      const file = input.files[0] ? await normalizeImageFile(input.files[0]) : null;
                      if (file) {
                        setQuickAddBackImage(file);
                        setQuickAddBackImagePreview(URL.createObjectURL(file));
                      }
                      input.value = '';
                    }}
                  />
                </div>
                <IdentifyStatus identifying={quickAddIdentifying} rereading={quickAddRereading} note={quickAddIdentifyNote} />
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Card Name <span style={{ color: '#999', fontWeight: '400' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={quickAddName}
                    onChange={(e) => setQuickAddName(e.target.value)}
                    placeholder="e.g., LeBron James, Pikachu..."
                    autoFocus
                    /* 16px keeps iOS Safari from auto-zooming the page on focus.
                       MyCards add-card modal is fixed in MyCards.css, but this
                       Navbar Quick Add Card modal is separate and was missed. */
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                {/* All detail fields, since auto-identify fills them from the
                    photos and people want to see (and fix) what it read. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div>
                    <label style={quickAddLabelStyle}>Year</label>
                    <input
                      type="number"
                      value={quickAddFields.year}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, year: e.target.value }))}
                      placeholder="e.g., 2020"
                      style={quickAddInputStyle}
                    />
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Brand</label>
                    <input
                      type="text"
                      value={quickAddFields.brand}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, brand: e.target.value }))}
                      placeholder="e.g., Topps, Panini"
                      style={quickAddInputStyle}
                    />
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Set Name</label>
                    <input
                      type="text"
                      value={quickAddFields.set_name}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, set_name: e.target.value }))}
                      placeholder="e.g., Topps Chrome"
                      style={quickAddInputStyle}
                    />
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Card Number</label>
                    <input
                      type="text"
                      value={quickAddFields.card_number}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, card_number: e.target.value }))}
                      placeholder="e.g., 101"
                      style={quickAddInputStyle}
                    />
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Category</label>
                    <select
                      value={quickAddFields.sport}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, sport: e.target.value }))}
                      style={quickAddInputStyle}
                    >
                      <option value="">None</option>
                      <option value="Sports">Sports</option>
                      <option value="Basketball">Basketball</option>
                      <option value="Football">Football</option>
                      <option value="Baseball">Baseball</option>
                      <option value="Hockey">Hockey</option>
                      <option value="Soccer">Soccer</option>
                      <option value="Pokemon">Pokemon</option>
                      <option value="Disney">Disney</option>
                      <option value="Gaming">Gaming</option>
                      <option value="Entertainment">Entertainment</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Grade</label>
                    <input
                      type="text"
                      value={quickAddFields.grade}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, grade: e.target.value }))}
                      placeholder="e.g., 10, 9.5"
                      style={quickAddInputStyle}
                    />
                  </div>
                  <div>
                    <label style={quickAddLabelStyle}>Grading Company</label>
                    <input
                      type="text"
                      value={quickAddFields.grading_company}
                      onChange={(e) => setQuickAddFields(prev => ({ ...prev, grading_company: e.target.value }))}
                      placeholder="e.g., PSA, BGS"
                      style={quickAddInputStyle}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={quickAddLabelStyle}>Notes</label>
                  <textarea
                    value={quickAddFields.notes}
                    onChange={(e) => setQuickAddFields(prev => ({ ...prev, notes: e.target.value }))}
                    rows="2"
                    placeholder="Personal notes about this card..."
                    style={{ ...quickAddInputStyle, resize: 'vertical' }}
                  />
                </div>
                <p style={{ fontSize: '12px', color: '#999', margin: '0 0 14px' }}>
                  Edit details anytime from{' '}
                  <button type="button" onClick={() => { closeQuickAdd(); navigate('/my-cards'); }} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', padding: 0, fontSize: '12px' }}>
                    My Cards
                  </button>.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={closeQuickAdd} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={quickAddSubmitting} style={{ flex: 2, padding: '10px', border: 'none', borderRadius: '8px', background: 'var(--color-primary)', color: '#fff', cursor: quickAddSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: quickAddSubmitting ? 0.7 : 1 }}>
                    {quickAddSubmitting ? 'Adding...' : 'Add Card'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Quick-add crop modal */}
        {quickAddCropSide && quickAddCropSrc && (
          <ImageCropper
            imageSrc={quickAddCropSrc}
            aspectRatio={2.5 / 3.5}
            title={`Crop ${quickAddCropSide === 'front' ? 'Front' : 'Back'} Image`}
            onCrop={(file, previewUrl) => {
              if (quickAddCropSide === 'front') {
                setQuickAddImage(file);
                setQuickAddImagePreview(previewUrl);
              } else {
                setQuickAddBackImage(file);
                setQuickAddBackImagePreview(previewUrl);
              }
              setQuickAddCropSide(null);
              setQuickAddCropSrc(null);
            }}
            onCancel={() => { setQuickAddCropSide(null); setQuickAddCropSrc(null); }}
          />
        )}

        {!isAuthenticated && !authLoading ? (
          isLandingRoute ? (
            <Link to="/login" className="navbar-link navbar-landing-login">
              Login
            </Link>
          ) : (
            <>
              <button
                className="mobile-menu-toggle"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
              <div className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`} role="menu">
                <Link to="/login" className="navbar-link" onClick={closeMobileMenu} role="menuitem">Login</Link>
                <Link
                  to="/register"
                  className="btn btn-primary navbar-signup-btn"
                  onClick={closeMobileMenu}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    minHeight: '36px',
                    height: 'auto',
                    width: 'auto',
                    maxWidth: 'fit-content',
                    lineHeight: '1.4',
                    display: 'inline-flex',
                    flexShrink: 0
                  }}
                  role="menuitem"
                >
                  Sign Up
                </Link>
              </div>
            </>
          )
        ) : null}
      </div>
    </nav>
  );
};

// Memoize Navbar component to prevent unnecessary re-renders
export default React.memo(Navbar);

